import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiBarChart2, FiInfo } from 'react-icons/fi';
import { 
  calculateTotalCost, 
  getSuggestedDonationAmount, 
  calculateBurritosFromDonation,
  COST_PER_BURRITO,
  TOTAL_FIXED_COSTS 
} from '../utils/costCalculator';

const CalculatorContainer = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 40px;
  margin: 40px 0;
`;

const CalculatorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--light-gray);
`;

const CalculatorIcon = styled.div`
  background: var(--primary-blue);
  color: white;
  padding: 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CalculatorTitle = styled.h3`
  font-size: 1.8rem;
  color: var(--primary-blue);
  margin: 0;
`;

const CalculatorTabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
`;

const TabButton = styled.button`
  padding: 12px 24px;
  border: 2px solid var(--primary-blue);
  background: ${props => props.active ? 'var(--primary-blue)' : 'white'};
  color: ${props => props.active ? 'white' : 'var(--primary-blue)'};
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--primary-blue);
    color: white;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: var(--dark-gray);
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 1.1rem;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: var(--primary-blue);
    outline: none;
  }
`;

const ResultsContainer = styled.div`
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 15px;
  padding: 25px;
  margin-top: 20px;
`;

const ResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #cbd5e1;

  &:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: 1.2rem;
    color: var(--primary-blue);
  }
`;

const ResultLabel = styled.span`
  color: var(--dark-gray);
`;

const ResultValue = styled.span`
  font-weight: 600;
  color: var(--primary-blue);
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, #fff3e0, #ffe0b2);
  border-left: 4px solid var(--accent-orange);
  padding: 20px;
  border-radius: 8px;
  margin: 20px 0;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const InfoIcon = styled(FiInfo)`
  color: var(--accent-orange);
  margin-top: 2px;
  flex-shrink: 0;
`;

const QuickOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 15px;
  margin: 20px 0;
`;

const QuickOptionButton = styled.button`
  padding: 15px 10px;
  border: 2px solid var(--light-gray);
  background: white;
  border-radius: 10px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-blue);
    background: #f8fafc;
  }

  .count {
    display: block;
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--primary-blue);
  }

  .cost {
    display: block;
    font-size: 0.9rem;
    color: var(--medium-gray);
    margin-top: 4px;
  }
`;

const CostCalculator = () => {
  const [activeTab, setActiveTab] = useState('burritos-to-cost');
  const [burritoCount, setBurritoCount] = useState(210);
  const [donationAmount, setDonationAmount] = useState(300);

  const handleBurritoCountChange = (e) => {
    const count = parseInt(e.target.value) || 0;
    setBurritoCount(count);
  };

  const handleDonationChange = (e) => {
    const amount = parseFloat(e.target.value) || 0;
    setDonationAmount(amount);
  };

  const handleQuickSelect = (count) => {
    setBurritoCount(count);
  };

  const costResults = {
    totalCost: calculateTotalCost(burritoCount),
    suggestedDonation: getSuggestedDonationAmount(burritoCount),
    costPerBurrito: COST_PER_BURRITO,
    fixedCosts: TOTAL_FIXED_COSTS
  };

  const donationResults = calculateBurritosFromDonation(donationAmount);

  return (
    <CalculatorContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <CalculatorHeader>
        <CalculatorIcon>
          <FiBarChart2 />
        </CalculatorIcon>
        <CalculatorTitle>Event Cost Calculator</CalculatorTitle>
      </CalculatorHeader>

      <CalculatorTabs>
        <TabButton 
          active={activeTab === 'burritos-to-cost'}
          onClick={() => setActiveTab('burritos-to-cost')}
        >
          Burritos → Cost
        </TabButton>
        <TabButton 
          active={activeTab === 'cost-to-burritos'}
          onClick={() => setActiveTab('cost-to-burritos')}
        >
          Donation → Burritos
        </TabButton>
      </CalculatorTabs>

      {activeTab === 'burritos-to-cost' && (
        <>
          <InputGroup>
            <Label htmlFor="burrito-count">Number of Burritos</Label>
            <Input
              id="burrito-count"
              type="number"
              value={burritoCount}
              onChange={handleBurritoCountChange}
              placeholder="Enter number of burritos"
              min="1"
            />
          </InputGroup>

          <QuickOptions>
            {[50, 100, 150, 210, 300, 400].map(count => (
              <QuickOptionButton 
                key={count} 
                onClick={() => handleQuickSelect(count)}
              >
                <span className="count">{count}</span>
                <span className="cost">${getSuggestedDonationAmount(count)}</span>
              </QuickOptionButton>
            ))}
          </QuickOptions>

          <ResultsContainer>
            <ResultRow>
              <ResultLabel>Ingredient Costs ({burritoCount} burritos):</ResultLabel>
              <ResultValue>${(burritoCount * COST_PER_BURRITO).toFixed(2)}</ResultValue>
            </ResultRow>
            <ResultRow>
              <ResultLabel>Fixed Event Costs:</ResultLabel>
              <ResultValue>${TOTAL_FIXED_COSTS.toFixed(2)}</ResultValue>
            </ResultRow>
            <ResultRow>
              <ResultLabel>Total Actual Cost:</ResultLabel>
              <ResultValue>${costResults.totalCost.toFixed(2)}</ResultValue>
            </ResultRow>
            <ResultRow>
              <ResultLabel>Suggested Donation Amount:</ResultLabel>
              <ResultValue>${costResults.suggestedDonation}</ResultValue>
            </ResultRow>
          </ResultsContainer>

          <InfoBox>
            <InfoIcon />
            <div>
              <strong>Cost Breakdown:</strong> ${COST_PER_BURRITO.toFixed(2)} per burrito 
              (ingredients) + ${TOTAL_FIXED_COSTS.toFixed(2)} fixed costs (gloves, tablecloths, foil). 
              Suggested donation is rounded to the nearest $25 (under $500) or $50 (over $500) 
              to simplify sponsorship amounts.
            </div>
          </InfoBox>
        </>
      )}

      {activeTab === 'cost-to-burritos' && (
        <>
          <InputGroup>
            <Label htmlFor="donation-amount">Donation Amount ($)</Label>
            <Input
              id="donation-amount"
              type="number"
              value={donationAmount}
              onChange={handleDonationChange}
              placeholder="Enter donation amount"
              min="0"
              step="25"
            />
          </InputGroup>

          <ResultsContainer>
            <ResultRow>
              <ResultLabel>Available for Ingredients:</ResultLabel>
              <ResultValue>${(donationAmount - TOTAL_FIXED_COSTS).toFixed(2)}</ResultValue>
            </ResultRow>
            <ResultRow>
              <ResultLabel>Number of Burritos Possible:</ResultLabel>
              <ResultValue>{donationResults.burritoCount}</ResultValue>
            </ResultRow>
            <ResultRow>
              <ResultLabel>Total Cost Used:</ResultLabel>
              <ResultValue>${donationResults.totalCost.toFixed(2)}</ResultValue>
            </ResultRow>
            <ResultRow>
              <ResultLabel>Remaining Funds:</ResultLabel>
              <ResultValue>${donationResults.remainingFunds.toFixed(2)}</ResultValue>
            </ResultRow>
          </ResultsContainer>

          <InfoBox>
            <InfoIcon />
            <div>
              <strong>How it works:</strong> Fixed costs (${TOTAL_FIXED_COSTS.toFixed(2)}) 
              are subtracted first, then remaining funds are divided by the cost per burrito 
              (${COST_PER_BURRITO.toFixed(2)}) to determine how many burritos can be made.
            </div>
          </InfoBox>
        </>
      )}
    </CalculatorContainer>
  );
};

export default CostCalculator; 